<template>
  <el-form :model="model" label-width="150px">
    <el-form-item label="Type" prop="type">
      <el-select
        v-model="virtualType"
        placeholder="Select type"
        class="w-full"
        clearable
      >
        <el-option label="Formula" value="formula" />
        <el-option label="Template" value="template" />
      </el-select>
    </el-form-item>
    <el-form-item label="Asset ID" prop="assetId">
      <el-input
        v-model="model.assetId"
        placeholder="Enter assetId"
        class="w-full"
        clearable
      />
    </el-form-item>
    <el-form-item label="Asset Image URL">
      <el-input
        v-model="model.assetImageUrl"
        placeholder="Enter asset image URL"
      >
        <template #append>
          <el-button
            icon="el-icon-link"
            @click="handleOpen(model.assetImageUrl)"
          />
        </template>
      </el-input>
    </el-form-item>
    <el-form-item label="Unit" prop="unit">
      <el-input
        v-model="model.unit"
        placeholder="Enter unit"
        class="w-full"
        clearable
      />
    </el-form-item>
    <el-form-item
      v-if="virtualType === 'formula'"
      label="Formula"
      prop="formula"
    >
      <el-input
        type="textarea"
        v-model="model.formula"
        autosize
        placeholder="Enter formula"
        class="w-full"
      />
    </el-form-item>
    <el-form-item v-else label="fluxTemplate" prop="fluxTemplate">
      <el-input
        type="textarea"
        v-model="model.fluxTemplate"
        autosize
        placeholder="Enter flux template"
        class="w-full"
      />
    </el-form-item>
    <el-form-item
      v-if="virtualType === 'formula'"
      label="Bindings"
      prop="bindings"
    >
      <el-input
        type="textarea"
        v-model="bindingsText"
        autosize
        placeholder='{"a":"11481:210 AC Total","b":"11515:209AC Total"}'
        class="w-full"
      />
    </el-form-item>
    <span class="flex flex-row justify-end">
      <el-button @click="handleCancel">Close</el-button>
      <el-button :loading="uploading" type="primary" @click="handleConfirm">
        Save
      </el-button>
    </span>
  </el-form>
</template>

<script>
import { createDeviceMetric, updateDeviceMetric } from "@/api";

class DeviceMetricRequestModel {
  assetId = undefined;
  type = "formula";
  unit = "";
  formula = "(${a}+${b})/3600000";
  fluxTemplate =
    'from(bucket: "device_data") |> range(start: ${start}, stop: ${end}) |> filter(fn: (r) => r["device_key"] == "ci:-1:other:egauge-energy-1563") |> filter(fn: (r) => r["_field"] == "210 AC Total:int") |> aggregateWindow(every: ${interval}, fn: first, createEmpty: false) |> difference() |> map(fn: (r) => ({ r with _value: float(v: if r._value < 0 then -1 * r._value else r._value) / 3600000.0, _field: "210 AC Total:float" })) |> timeShift(duration: -${interval})';
  bindings = undefined;
  assetImageUrl = "";
  name = "";
  deviceKey = "";
  constructor(model) {
    if (model) {
      Object.assign(this, model);
    }
  }
}

// 自动扫描 ${xxx} 并赋值
function getBindingsObject(formula, asset = {}) {
  // 从公式中提取绑定对象
  let bindingsObj = {};
  // 自动扫描 ${xxx} 并赋值
  const matches = formula.match(/\$\{([a-zA-Z0-9_]+)\}/g);
  if (matches) {
    matches.forEach((m) => {
      const key = m.replace(/\$\{|\}/g, "");
      // 优先取 asset.attr[key]，否则为空字符串
      bindingsObj[key] = asset.attr[key] || "";
    });
  }
  return bindingsObj;
}

function assetMapToSubmitModel(virtualType, asset) {
  // 统一提交模型映射
  if (!asset || !asset.attr) {
    return new DeviceMetricRequestModel();
  }

  let formula = asset.attr.formula || asset.attr.flux_template || "";
  let bindingsObj = getBindingsObject(formula, asset);

  /**
   * 两种提交模型根据virtual_type返回
   * template
   * ```json
   * {
   * "assetId": 11481,
   * "type": "template",
   * "unit":"kWh",
   * "fluxTemplate":"from(bucket: \"device_data\") |> range(start: ${start}, stop: ${end}) |> filter(fn: (r) => r[\"device_key\"] == \"ci:-1:other:egauge-energy-1563\") |> filter(fn: (r) => r[\"_field\"] == \"210 AC Total:int\") |> aggregateWindow(every: ${interval}, fn: first, createEmpty: false) |> difference() |> map(fn: (r) => ({ r with _value: float(v: if r._value < 0 then -1 * r._value else r._value) / 3600000.0, _field: \"210 AC Total:float\" })) |> timeShift(duration: -${interval})"
   * }
   *```
   * formula
   *```json
   * {
   * "assetId": 11482,
   * "type": "formula",
   * "unit":"kWh",
   * "formula":"(${a}+${b})/3600000",
   * "bindings": {
   *   "a":"11481:210 AC Total",
   *   "b":"11515:209 AC Total",
   * }
   * ```
   */
  const submitModel = new DeviceMetricRequestModel({
    assetId: asset.assetId,
    type: asset.attr.virtual_type || virtualType || "formula",
    unit: asset.attr.unit || "",

    assetImageUrl: asset.attr.asset_img || "",
    name: asset.name || "",
    deviceKey: asset.deviceKey || "",
  });

  if (virtualType === "formula") {
    submitModel.formula = formula;
    submitModel.bindings = bindingsObj;
  } else {
    submitModel.fluxTemplate = formula;
  }

  return submitModel;
}

export default {
  name: "MetricEdit",
  components: {},
  emits: ["input", "confirm", "cancel"],
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    virtualType(newVal) {
      const newModel = assetMapToSubmitModel(newVal, this.asset);
      this.model = newModel;
    },
    'model.formula'(newVal) {
      if (this.virtualType === "formula") {
        this.model.bindings = getBindingsObject(newVal, this.asset);
      }
    },
  },
  data() {
    let model;
    try {
      model = assetMapToSubmitModel(
        (this.asset && this.asset.attr && this.asset.attr.virtual_type) ||
          "template",
        this.asset
      );
    } catch {
      model = new DeviceMetricRequestModel();
    }
    return {
      virtualType:
        (this.asset && this.asset.attr && this.asset.attr.virtual_type) ||
        "template",
      model,
      uploading: false,
    };
  },
  computed: {
    bindingsText: {
      get() {
        // 显示时始终为字符串
        if (typeof this.model.bindings === "string") {
          return this.model.bindings;
        }
        try {
          return JSON.stringify(this.model.bindings, null, 2);
        } catch {
          return "{}";
        }
      },
      set(value) {
        // 编辑时转为对象
        try {
          this.model.bindings = JSON.parse(value);
        } catch {
          this.model.bindings = {};
        }
      },
    },
  },
  methods: {
    handleOpen(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },
    async handleConfirm() {
      const me = this;
      // 保证提交时 bindings 为对象
      if (typeof me.model.bindings === "string") {
        try {
          me.model.bindings = JSON.parse(me.model.bindings);
        } catch {
          me.model.bindings = {};
        }
      }
      // 只提交对应类型的字段
      const submitData = { ...me.model };
      submitData.type = me.virtualType;
      if (me.virtualType === "formula") {
        delete submitData.fluxTemplate;
      } else {
        delete submitData.formula;
      }
      try {
        me.uploading = true;
        if (me.asset) {
          await updateDeviceMetric(submitData);
        } else {
          await createDeviceMetric(submitData);
        }
      } finally {
        me.uploading = false;
      }
      me.$emit("confirm", submitData);
    },
    handleCancel() {
      this.$emit("cancel");
    },
  },
};
</script>
